import { Container } from 'ui';
import { Divider } from 'omakase';
import { FunctionComponent } from 'react';
import { Head } from 'head/Head';
import { LegalPageType, useCmsLegalPageQuery } from 'generated/graphql';
import { css } from '@emotion/react';
import { getTypographyVariant } from 'theme/typography/omakaseTypography';
import { gql } from '@apollo/client';
import { renderLegalContentHtml } from 'routes/legal/legalTiptapRenderer';
import DOMPurify from 'isomorphic-dompurify';

interface LegalPageHeadMeta {
  title: string;
  description: string;
  keywords?: string;
}

// The Tock consumer site only serves these seven legal pages; JCE-only types
// (e.g. PAYMENT_TERMS, ACCESSIBILITY_STATEMENT) live in the JCE experience and are
// never routed here, so they are intentionally excluded rather than given empty
// Tock metadata. `Extract` keeps each key a real LegalPageType (catching typos)
// while decoupling this record from future JCE-only enum growth.
export type TockLegalPageType = Extract<
  LegalPageType,
  | 'TERMS_OF_USE'
  | 'PRIVACY_POLICY'
  | 'PRIVACY_SPECIFIC'
  | 'COOKIE_POLICY'
  | 'MERCHANT_DPA'
  | 'MERCHANT_TERMS'
  | 'AFFILIATE'
>;

// SEO/social <head> metadata per Tock legal page type. Keyed by TockLegalPageType so
// a new Tock legal type is a compile error until its title/description are supplied.
// The page body itself is CMS-authored; only this framing metadata is code-shipped.
const LEGAL_PAGE_HEAD: Record<TockLegalPageType, LegalPageHeadMeta> = {
  AFFILIATE: {
    title: 'Tock - Affiliate Marketing Program Master Terms',
    description: 'Affiliate marketing program master terms',
  },
  COOKIE_POLICY: {
    title: 'Tock - Cookie Policy',
    description: 'Tock cookie policy',
  },
  MERCHANT_DPA: {
    title: 'Tock - Merchant Data Processing Addendum',
    description: 'Tock merchant data processing addendum',
  },
  MERCHANT_TERMS: {
    title: 'Tock - Merchant Terms',
    description: 'Tock merchant terms',
  },
  PRIVACY_POLICY: {
    title: 'Tock - Privacy Policy',
    description: 'Tock privacy policy',
    keywords: 'privacy policy, terms, conditions',
  },
  PRIVACY_SPECIFIC: {
    title: 'Tock - Specific Privacy Notices',
    description: 'Tock specific privacy notices',
  },
  TERMS_OF_USE: {
    title: 'Tock - Terms',
    description: 'Tock terms of service',
    keywords: 'terms of service, terms, conditions',
  },
};

gql`
  query CmsLegalPage($type: LegalPageType!) @alertOwner(value: GUEST) {
    enUs: legalPage(app: TOCK, locale: "en-US", type: $type) {
      id
      content
      effectiveDate
    }
    frCa: legalPage(app: TOCK, locale: "fr-CA", type: $type) {
      id
      content
      effectiveDate
    }
  }
`;

type SupportedLocale = 'en-US' | 'fr-CA';

// The effective date is always displayed in America/Chicago (CST/CDT), matching
// the authoring convention, regardless of the reader's timezone.
const EFFECTIVE_DATE_TIME_ZONE = 'America/Chicago';

const EFFECTIVE_DATE_PREFIX: Record<SupportedLocale, string> = {
  'en-US': 'Effective Date: ',
  'fr-CA': "Date d'entrée en vigueur : ",
};

const legalContentStyles = css`
  ${getTypographyVariant('body02')};
  overflow-wrap: break-word;

  h1 {
    ${getTypographyVariant('heading01')};
    margin: 24px 0 16px;
  }

  h2 {
    ${getTypographyVariant('heading02')};
    margin: 24px 0 12px;
  }

  h3 {
    ${getTypographyVariant('heading03')};
    margin: 20px 0 12px;
  }

  p {
    ${getTypographyVariant('body02')};
    margin: 0 0 16px;
  }

  ul,
  ol {
    ${getTypographyVariant('body02')};
    margin: 0 0 16px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0 0 16px;
  }

  th,
  td {
    border: 1px solid currentColor;
    padding: 8px;
    text-align: left;
    vertical-align: top;
  }

  .legal-effective-date {
    ${getTypographyVariant('body01Strong')};
    margin: 0 0 24px;
  }
`;

function formatEffectiveDate(locale: SupportedLocale, isoInstant: string): string {
  const formatted = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: EFFECTIVE_DATE_TIME_ZONE,
  }).format(new Date(isoInstant));

  return `${EFFECTIVE_DATE_PREFIX[locale]}${formatted}`;
}

/**
 * Inject the localized effective-date line as an HTML string immediately after
 * the first heading in the generated content. The label + Intl-formatted date
 * are constants; the concatenated result is re-run through `DOMPurify.sanitize`
 * in `buildLocaleHtml`, so the sanitizer remains the last write before the
 * `dangerouslySetInnerHTML` sink.
 */
function injectEffectiveDate(
  html: string,
  locale: SupportedLocale,
  isoInstant: string | null | undefined
): string {
  if (!isoInstant) {
    return html;
  }

  const line = `<p class="legal-effective-date" data-testid="legal-effective-date-${locale}">${formatEffectiveDate(
    locale,
    isoInstant
  )}</p>`;

  const firstHeadingClose = html.match(/<\/h[1-6]>/i);
  if (firstHeadingClose && firstHeadingClose.index !== undefined) {
    const insertAt = firstHeadingClose.index + firstHeadingClose[0].length;
    return `${html.slice(0, insertAt)}${line}${html.slice(insertAt)}`;
  }

  return `${line}${html}`;
}

interface LegalPageContent {
  content: string;
  effectiveDate?: string | null;
}

function buildLocaleHtml(
  locale: SupportedLocale,
  page: LegalPageContent | null | undefined
): string | null {
  if (!page?.content) {
    return null;
  }

  const html = renderLegalContentHtml(page.content);
  if (!html) {
    return null;
  }

  return DOMPurify.sanitize(injectEffectiveDate(html, locale, page.effectiveDate));
}

export interface CmsLegalPageProps {
  pageType: TockLegalPageType;
}

/**
 * Renders a CMS-authored legal page (TipTap JSON -> sanitized HTML) for the
 * given `pageType`, stacking the en-US and fr-CA versions with a divider between
 * them, and emitting the page's SEO `<head>` metadata via {@link Head}. Gated
 * behind the `CMS_LEGAL_PAGES` flag by `FeatureGatedLegalPage`.
 */
const CmsLegalPage: FunctionComponent<CmsLegalPageProps> = ({ pageType }) => {
  const headMeta = LEGAL_PAGE_HEAD[pageType];
  const { data, loading } = useCmsLegalPageQuery({ variables: { type: pageType } });

  const enUsHtml = loading ? null : buildLocaleHtml('en-US', data?.enUs);
  const frCaHtml = loading ? null : buildLocaleHtml('fr-CA', data?.frCa);

  return (
    <>
      <Head
        title={headMeta.title}
        description={headMeta.description}
        keywords={headMeta.keywords}
      />
      {(enUsHtml || frCaHtml) && (
        <div className="Legal">
          <Container maxWidth="md">
            <div css={legalContentStyles}>
              {enUsHtml && <div dangerouslySetInnerHTML={{ __html: enUsHtml }} />}
              {enUsHtml && frCaHtml && <Divider data-testid="legal-locale-divider" tall />}
              {frCaHtml && <div dangerouslySetInnerHTML={{ __html: frCaHtml }} />}
            </div>
          </Container>
        </div>
      )}
    </>
  );
};

export default CmsLegalPage;
